/**
 * Hook to check provider configuration status for onboarding/settings.
 *
 * Uses INTEGRATION_REGISTRY so any new integration automatically participates
 * in the "needs setup" check without touching this file.
 */

import { useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { INTEGRATION_REGISTRY, type IntegrationDefinition } from "@/integrations/registry";

interface ProviderConfigStatus {
  isLoading: boolean;
  hasError: boolean;
  needsIntegrationSetup: boolean;
  hasSecurityDashboard: boolean;
  hasConfiguredProviders: boolean;
  providerCount: number;
  /** Per-integration connection counts keyed by integration id */
  counts: Record<string, number>;
}

export function useProviderConfigStatus(): ProviderConfigStatus {
  const { data: featureFlags, isLoading: flagsLoading, error: flagsError } = useFeatureFlags();
  const hasSecurityDashboard = featureFlags?.security_dashboard === true;

  const queries = useQueries({
    queries: INTEGRATION_REGISTRY.map((def: IntegrationDefinition) => ({
      queryKey: [def.queryKey, 1, 10],
      queryFn: () => def.listConnections(1, 10),
      staleTime: 30_000,
      enabled: hasSecurityDashboard,
    })),
  });

  return useMemo(() => {
    const anyLoading = queries.some((q: UseQueryResult) => q.isLoading);
    const anyError = queries.some((q: UseQueryResult) => q.error);

    const isLoading = flagsLoading || (hasSecurityDashboard && anyLoading);
    const hasError = !!(flagsError || (hasSecurityDashboard && anyError));

    const counts: Record<string, number> = {};
    let providerCount = 0;
    for (let i = 0; i < INTEGRATION_REGISTRY.length; i++) {
      const def = INTEGRATION_REGISTRY[i];
      const data = queries[i]?.data as { data?: unknown[] } | undefined;
      const count = data?.data?.length ?? 0;
      counts[def.id] = count;
      providerCount += count;
    }

    const hasConfiguredProviders = providerCount > 0;
    const needsIntegrationSetup = hasSecurityDashboard && !hasConfiguredProviders;

    return {
      isLoading,
      hasError,
      needsIntegrationSetup,
      hasSecurityDashboard,
      hasConfiguredProviders,
      providerCount,
      counts,
    };
  }, [flagsLoading, flagsError, hasSecurityDashboard, queries]);
}
